import React, { useState } from 'react';
import { 
    Box, 
    Button, 
    Typography, 
    TextField, 
    Grid, 
    Card, 
    CardContent,
    FormHelperText,
    Paper,
    Chip,
    Autocomplete,
    InputAdornment
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import LanguageIcon from '@mui/icons-material/Language';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

// Suggested options for features and event types (these can be expanded)
const suggestedFeatures = [
    'Sound System',
    'WiFi',
    'Projector',
    'Stage',
    'Lighting Equipment',
    'Kitchen Access',
    'Outdoor Space',
    'Accessible Entrance',
    'Catering Kitchen',
    'Parking',
    'Tables & Chairs',
    'Air Conditioning',
    'Heating',
    'Natural Light',
    'Bar Area'
];

const suggestedEventTypes = [
    'Corporate Event',
    'Conference',
    'Birthday Party',
    'Holiday Party',
    'Networking Event',
    'Workshop',
    'Seminar',
    'Product Launch',
    'Photoshoot',
    'Film Shooting',
    'Cocktail Party',
    'Gala',
    'Exhibition',
    'Concert'
];

/**
 * First step of venue onboarding - basic venue information
 */
export const BasicInformationStep = ({ 
    venueName, 
    setVenueName,
    venueDescription,
    setVenueDescription,
    coverPhoto,
    setCoverPhoto,
    otherPhotos,
    setOtherPhotos,
    features,
    setFeatures,
    eventTypes,
    setEventTypes,
    website,
    setWebsite,
    instagram,
    setInstagram,
    linkedin,
    setLinkedin,
    facebook,
    setFacebook,
    email,
    setEmail,
    phoneNumber,
    setPhoneNumber,
    errors,
}) => {
    // Local state for input values
    const [featureInput, setFeatureInput] = useState('');
    const [eventTypeInput, setEventTypeInput] = useState('');

    // Handle cover photo upload
    const handleCoverPhotoChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setCoverPhoto(e.target.files[0]);
        }
    };

    // Handle other photos upload
    const handleOtherPhotosChange = (e) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            setOtherPhotos([...otherPhotos, ...filesArray]);
        }
    };

    // Remove other photo by index
    const handleRemoveOtherPhoto = (index) => {
        const updatedPhotos = [...otherPhotos];
        updatedPhotos.splice(index, 1);
        setOtherPhotos(updatedPhotos);
    };

    // Remove cover photo
    const handleRemoveCoverPhoto = () => {
        setCoverPhoto(null);
    };

    // Handle adding a feature tag
    const handleAddFeature = (newValue) => {
        if (newValue && !features.includes(newValue)) {
            setFeatures([...features, newValue]);
        }
        setFeatureInput('');
    };

    // Handle adding an event type tag
    const handleAddEventType = (newValue) => {
        if (newValue && !eventTypes.includes(newValue)) {
            setEventTypes([...eventTypes, newValue]);
        }
        setEventTypeInput('');
    };

    // Handle feature input change
    const handleFeatureInputChange = (e) => {
        setFeatureInput(e.target.value);
    };

    // Handle event type input change
    const handleEventTypeInputChange = (e) => {
        setEventTypeInput(e.target.value);
    };

    // Handle keydown to add tag on Enter
    const handleFeatureKeyDown = (e) => {
        if (e.key === 'Enter' && featureInput.trim()) {
            e.preventDefault();
            handleAddFeature(featureInput.trim());
        }
    };

    // Handle keydown to add tag on Enter
    const handleEventTypeKeyDown = (e) => {
        if (e.key === 'Enter' && eventTypeInput.trim()) {
            e.preventDefault();
            handleAddEventType(eventTypeInput.trim());
        }
    };

    // Handle delete feature
    const handleDeleteFeature = (featureToDelete) => {
        setFeatures(features.filter((feature) => feature !== featureToDelete));
    };

    // Handle delete event type
    const handleDeleteEventType = (eventTypeToDelete) => {
        setEventTypes(eventTypes.filter((eventType) => eventType !== eventTypeToDelete));
    };

    return (
        <Card sx={{ mb: 4 }}>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    Basic Venue Information
                </Typography>
                
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Venue Name"
                            value={venueName}
                            onChange={(e) => setVenueName(e.target.value)}
                            required
                            error={!!errors.venueName}
                            helperText={errors.venueName || "The name of your venue"}
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Venue Description"
                            value={venueDescription}
                            onChange={(e) => setVenueDescription(e.target.value)}
                            multiline
                            rows={4}
                            required
                            error={!!errors.venueDescription}
                            helperText={errors.venueDescription || "Describe your venue, its atmosphere, and what makes it special"}
                            placeholder="Describe your venue's unique features, atmosphere, and what types of events it's best suited for..."
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" gutterBottom>
                            Venue Features
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Add features your venue offers (e.g., "WiFi", "Sound System", "Projector")
                        </Typography>
                        
                        <Autocomplete
                            multiple
                            id="features-tags"
                            options={suggestedFeatures.filter(option => !features.includes(option))}
                            value={features}
                            freeSolo
                            onChange={(event, newValue) => setFeatures(newValue)}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        key={option}
                                        label={option}
                                        {...getTagProps({ index })}
                                        onDelete={() => handleDeleteFeature(option)}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Features"
                                    placeholder="Type and press Enter"
                                    onChange={handleFeatureInputChange}
                                    onKeyDown={handleFeatureKeyDown}
                                    value={featureInput}
                                    fullWidth
                                    error={!!errors.features}
                                    helperText={errors.features}
                                />
                            )}
                        />
                        
                        {features.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Added features: {features.length}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" gutterBottom>
                            Event Types
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            What types of events is your venue suitable for?
                        </Typography>
                        
                        <Autocomplete
                            multiple
                            id="event-type-tags"
                            options={suggestedEventTypes.filter(option => !eventTypes.includes(option))}
                            value={eventTypes}
                            freeSolo
                            onChange={(event, newValue) => setEventTypes(newValue)}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        key={option}
                                        label={option}
                                        {...getTagProps({ index })}
                                        onDelete={() => handleDeleteEventType(option)}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Event Types"
                                    placeholder="Type and press Enter"
                                    onChange={handleEventTypeInputChange}
                                    onKeyDown={handleEventTypeKeyDown}
                                    value={eventTypeInput}
                                    fullWidth
                                    error={!!errors.eventTypes}
                                    helperText={errors.eventTypes}
                                />
                            )}
                        />
                        
                        {eventTypes.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Added event types: {eventTypes.length}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Cover Photo <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            This will be the main image displayed for your venue. Choose your best photo.
                        </Typography>
                        
                        {coverPhoto ? (
                            <Box 
                                sx={{ 
                                    position: 'relative',
                                    width: '100%',
                                    height: 300,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    mb: 2,
                                    backgroundColor: '#f5f5f5'
                                }}
                            >
                                <IconButton
                                    size="small"
                                    sx={{ 
                                        position: 'absolute', 
                                        top: 8, 
                                        right: 8,
                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.7)'
                                        }
                                    }}
                                    onClick={handleRemoveCoverPhoto}
                                >
                                    <DeleteIcon />
                                </IconButton>
                                <img 
                                    src={URL.createObjectURL(coverPhoto)} 
                                    alt="Venue Cover" 
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover' 
                                    }} 
                                />
                            </Box>
                        ) : (
                            <Paper 
                                variant="outlined" 
                                sx={{ 
                                    p: 3, 
                                    mb: 2, 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: 200,
                                    backgroundColor: errors.coverPhoto ? '#fff8f8' : '#f8f8f8',
                                    borderColor: errors.coverPhoto ? 'error.main' : 'inherit'
                                }}
                            >
                                <CloudUploadIcon color="action" sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
                                <Typography color="text.secondary" align="center" gutterBottom>
                                    Drag and drop your cover photo here, or click to select
                                </Typography>
                                <Button
                                    component="label"
                                    variant="contained"
                                    size="large"
                                    sx={{ mt: 2 }}
                                >
                                    Upload Cover Photo
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleCoverPhotoChange}
                                    />
                                </Button>
                            </Paper>
                        )}
                        
                        {errors.coverPhoto && (
                            <FormHelperText error>{errors.coverPhoto}</FormHelperText>
                        )}
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Additional Venue Photos
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Upload additional photos to showcase different aspects of your venue (recommended)
                        </Typography>
                        
                        {otherPhotos.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {otherPhotos.map((photo, index) => (
                                    <Box 
                                        key={index} 
                                        sx={{ 
                                            position: 'relative',
                                            width: 150,
                                            height: 120,
                                            borderRadius: 1,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            sx={{ 
                                                position: 'absolute', 
                                                top: 5, 
                                                right: 5,
                                                backgroundColor: 'rgba(0,0,0,0.5)',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0,0,0,0.7)'
                                                }
                                            }}
                                            onClick={() => handleRemoveOtherPhoto(index)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                        <img 
                                            src={URL.createObjectURL(photo)} 
                                            alt={`Venue Photo ${index}`} 
                                            style={{ 
                                                width: '100%', 
                                                height: '100%', 
                                                objectFit: 'cover' 
                                            }} 
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                        
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            sx={{ mt: 1 }}
                        >
                            Upload Additional Photos
                            <input
                                type="file"
                                hidden
                                multiple
                                accept="image/*"
                                onChange={handleOtherPhotosChange}
                            />
                        </Button>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Contact Information
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Website URL"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LanguageIcon />
                                    </InputAdornment>
                                ),
                            }}
                            error={!!errors.website}
                            helperText={errors.website}
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon />
                                    </InputAdornment>
                                ),
                            }}
                            error={!!errors.email}
                            helperText={errors.email}
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PhoneIcon />
                                    </InputAdornment>
                                ),
                            }}
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber}
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                            Social Media Links
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Instagram"
                            value={instagram}
                            onChange={(e) => setInstagram(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <InstagramIcon />
                                    </InputAdornment>
                                ),
                            }}
                            error={!!errors.instagram}
                            helperText={errors.instagram}
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="LinkedIn"
                            value={linkedin}
                            onChange={(e) => setLinkedin(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LinkedInIcon />
                                    </InputAdornment>
                                ),
                            }}
                            error={!!errors.linkedin}
                            helperText={errors.linkedin}
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Facebook"
                            value={facebook}
                            onChange={(e) => setFacebook(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FacebookIcon />
                                    </InputAdornment>
                                ),
                            }}
                            error={!!errors.facebook}
                            helperText={errors.facebook}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};